import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, HttpError } from '../api/client';
import { useEditor } from '../store/editor';
import { useStoreSlice } from '../store/storeRegistry';
import Canvas from '../components/Canvas';
import ElementPanel from '../components/ElementPanel';
import FilterEditor from '../components/FilterEditor';
import SampleProductTabs from '../components/SampleProductTabs';
import SampleRenderPanel from '../components/SampleRenderPanel';
import StoreSelector from '../components/StoreSelector';
import Toast from '../components/Toast';
import { shouldDeselectOnKey } from '../lib/editorKeys';
import { productBoxPatch } from '../lib/productBox';
import { productSplit } from '../api/types';
import type { DesignConfig, LifestyleReason, LifestyleState } from '../api/types';

export default function Editor() {
  const { name = '' } = useParams<{ name: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const {
    config, dirty, setConfig, patchElement, patchFilter, patchMeta, patchAssets, markClean,
    addCustomElement, removeElement, undo, redo, canUndo, canRedo,
    enableLifestyleMode, disableLifestyleMode, patchLifestyle,
  } = useEditor();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [mobilePane, setMobilePane] = useState<'elements' | 'preview' | 'targeting'>('preview');
  const [productId, setProductId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  // What the LAST render actually did with the lifestyle photo. Needed because
  // an unresolved photo comes back as a valid full-canvas ad (see PreviewResult).
  const [lifestyleState, setLifestyleState] = useState<LifestyleState>('off');
  const [lifestyleReason, setLifestyleReason] = useState<LifestyleReason | null>(null);
  const [uploading, setUploading] = useState<'background' | 'logo' | 'lifestyle' | null>(null);
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const decodedName = useMemo(() => decodeURIComponent(name), [name]);
  const { selectedStoreId } = useStoreSlice();

  // Load config
  const cfgQ = useQuery({
    queryKey: ['config', selectedStoreId, decodedName],
    queryFn: () => api.getConfig(decodedName, selectedStoreId),
  });
  useEffect(() => {
    if (cfgQ.data) setConfig(cfgQ.data);
  }, [cfgQ.data, setConfig]);

  // Load sample products
  const samplesQ = useQuery({
    queryKey: ['samples', selectedStoreId],
    queryFn: () => api.sampleProducts(selectedStoreId),
  });
  useEffect(() => {
    if (!productId && samplesQ.data && samplesQ.data.length > 0) {
      setProductId(samplesQ.data[0].id);
    }
  }, [samplesQ.data, productId]);

  // Load source fields (feed + custom) for the active store
  const fieldsQ = useQuery({
    queryKey: ['source-fields', selectedStoreId],
    queryFn: () => api.storeFields(selectedStoreId),
  });

  // Store default cutout mode, so the product-image "Fit to content" control
  // can tell an explicit design choice from one inherited off the store.
  const storesQ = useQuery({ queryKey: ['stores'], queryFn: api.listStores });

  // Debounced preview refresh on config or product change
  useEffect(() => {
    if (!config || !productId) return;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      setPreviewing(true);
      try {
        const res = await api.preview(productId, config, selectedStoreId);
        setPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(res.blob);
        });
        setLifestyleState(res.state);
        setLifestyleReason(res.reason);
      } catch (err) {
        if (err instanceof HttpError) {
          // suppress 404s during initial load when assets aren't available
          if (err.status !== 404) {
            setToast({ kind: 'error', msg: `Preview failed: ${err.detail}` });
          }
        }
      } finally {
        setPreviewing(false);
      }
    }, 600);
    return () => {
      if (previewTimer.current) clearTimeout(previewTimer.current);
    };
  }, [config, productId, selectedStoreId]);

  // Cleanup blob URL on unmount
  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  // Undo / redo keyboard shortcuts (⌘Z / ⌘⇧Z, Ctrl on Linux/Win)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (shouldDeselectOnKey(e)) {
        setSelectedKey(null);
        return;
      }
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  // PUT to the LOADED name (decodedName): if the user renamed the config in
  // the meta panel, the server moves it (save new + delete old, W-C [UX-2])
  // and we navigate to the new URL instead of letting the old query refetch
  // a 404 and revert the screen.
  const save = useMutation({
    mutationFn: (cfg: DesignConfig) => api.putConfig(cfg, selectedStoreId, decodedName),
    onSuccess: (_data, cfg) => {
      markClean();
      qc.invalidateQueries({ queryKey: ['configs', selectedStoreId] });
      if (cfg.name !== decodedName) {
        // Leave the old-name query alone: removing/invalidating it while its
        // observer is still mounted refetches the just-deleted config (404).
        // It simply garbage-collects once we navigate away.
        nav(`/edit/${encodeURIComponent(cfg.name)}`, { replace: true });
      } else {
        qc.invalidateQueries({ queryKey: ['config', selectedStoreId, decodedName] });
      }
      setToast({ kind: 'success', msg: cfg.name !== decodedName ? `Saved as "${cfg.name}".` : 'Saved.' });
      setTimeout(() => setToast(null), 2200);
    },
    onError: (err: unknown) => {
      const detail = err instanceof HttpError ? err.detail : 'unknown error';
      setToast({ kind: 'error', msg: `Save failed: ${detail}` });
    },
  });

  // W-C [UX-11]: refresh/close with unsaved edits prompts the browser dialog.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  async function uploadAsset(kind: 'background' | 'logo' | 'lifestyle', file: File) {
    setUploading(kind);
    try {
      const res = await api.uploadAsset(kind, file, selectedStoreId);
      if (kind === 'lifestyle') {
        // The lifestyle photo is a per-config element knob, not one of the two
        // brand assets — it belongs in elements.lifestyle.asset so it rides
        // config.elements into config_hash.
        // field:null makes the single source control read as "uploaded image"
        // — an asset already WINS over a field, so leaving the old field set
        // would show one thing in the dropdown and render another.
        patchLifestyle({ asset: res.path, field: null });
      } else {
        patchAssets({ [kind]: res.path });
      }
      setToast({ kind: 'success', msg: `${kind} uploaded.` });
      setTimeout(() => setToast(null), 1500);
    } catch (err) {
      const detail = err instanceof HttpError ? err.detail : 'unknown error';
      setToast({ kind: 'error', msg: `Upload failed: ${detail}` });
    } finally {
      setUploading(null);
    }
  }

  if (cfgQ.isLoading || !config) {
    return (
      <div className="min-h-screen grid place-items-center bg-ff-ivory">
        <div className="text-ff-mute font-mono text-xs uppercase tracking-widish">
          Loading editor…
        </div>
      </div>
    );
  }

  if (cfgQ.isError) {
    return (
      <div className="min-h-screen grid place-items-center bg-ff-ivory">
        <div className="text-center">
          <p className="font-display text-3xl text-ff-forest">Couldn't load this config.</p>
          <Link to="/" className="ff-btn-ghost mt-4">
            ← Back to list
          </Link>
        </div>
      </div>
    );
  }

  const isDefault = config.name === '_default';
  // The width the product half renders at — shared with the Canvas so the drag
  // handle and the panel's X/Y/W/H fields can never resolve the box against
  // different widths (a panel edit would otherwise rewrite a dragged box).
  const [productCanvasW] = productSplit(config.elements, lifestyleState);

  return (
    <div className="min-h-screen bg-ff-ivory relative">
      <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" aria-hidden="true" />

      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-ff-line bg-ff-paper/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1480px] items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-4 min-w-0">
            <button
              type="button"
              onClick={() => nav('/')}
              className="flex items-center gap-1 text-xs font-semibold text-ff-mute hover:text-ff-ink"
            >
              ← <span className="hidden sm:inline">Configs</span>
            </button>
            <span className="h-6 w-px bg-ff-line" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-widish text-ff-mute">
                Editing
              </p>
              <p className="truncate font-display text-base font-bold text-ff-ink sm:text-lg">
                {config.name}
              </p>
            </div>
          </div>
          <div className="hidden items-center gap-2 sm:flex">
            <StoreSelector />
            <span className="mx-1 h-6 w-px bg-ff-line" aria-hidden="true" />
            {dirty ? (
              <span className="ff-chip bg-ff-accent-soft text-ff-accent">
                <span className="h-1.5 w-1.5 rounded-full bg-ff-accent animate-pulse" />
                Unsaved
              </span>
            ) : (
              <span className="ff-chip bg-ff-success-soft text-ff-success">Saved</span>
            )}
            <button
              type="button"
              onClick={undo}
              disabled={!canUndo()}
              className="ff-btn-ghost disabled:opacity-40 disabled:cursor-not-allowed"
              title="Undo (⌘Z)"
              aria-label="Undo"
              data-testid="undo-button"
            >
              ↶ Undo
            </button>
            <button
              type="button"
              onClick={redo}
              disabled={!canRedo()}
              className="ff-btn-ghost disabled:opacity-40 disabled:cursor-not-allowed"
              title="Redo (⌘⇧Z)"
              aria-label="Redo"
              data-testid="redo-button"
            >
              ↷ Redo
            </button>
            <button
              type="button"
              onClick={() => cfgQ.refetch().then((r) => r.data && setConfig(r.data))}
              disabled={!dirty || save.isPending}
              className="ff-btn-ghost"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={() => save.mutate(config)}
              disabled={!dirty || save.isPending}
              className="ff-btn-primary"
              data-testid="save-config"
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
          <div className="flex items-center gap-2 sm:hidden">
            <span
              className={`h-2 w-2 rounded-full ${dirty ? 'bg-ff-orange animate-pulse' : 'bg-ff-success'}`}
              aria-label={dirty ? 'Unsaved changes' : 'Saved'}
            />
            <button
              type="button"
              onClick={() => save.mutate(config)}
              disabled={!dirty || save.isPending}
              className="ff-btn-primary px-4 py-2"
              data-testid="save-config-mobile"
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto border-t border-ff-line px-4 py-2.5 sm:hidden">
          <StoreSelector compact />
          <button
            type="button"
            onClick={undo}
            disabled={!canUndo()}
            className="ff-btn-ghost whitespace-nowrap px-3 py-2 disabled:opacity-40"
            aria-label="Undo"
          >
            ↶ Undo
          </button>
          <button
            type="button"
            onClick={redo}
            disabled={!canRedo()}
            className="ff-btn-ghost whitespace-nowrap px-3 py-2 disabled:opacity-40"
            aria-label="Redo"
          >
            ↷ Redo
          </button>
          <button
            type="button"
            onClick={() => cfgQ.refetch().then((r) => r.data && setConfig(r.data))}
            disabled={!dirty || save.isPending}
            className="ff-btn-ghost whitespace-nowrap px-3 py-2 disabled:opacity-40"
          >
            Discard
          </button>
        </div>
      </header>

      {/* 3-pane workspace */}
      <main className="relative mx-auto grid max-w-[1480px] grid-cols-1 gap-5 px-4 py-5 sm:px-6 sm:py-8 xl:grid-cols-[280px_minmax(0,1fr)_336px] xl:gap-6">
        <nav
          className="ff-segment sticky top-[7.5rem] z-30 col-span-full grid grid-cols-3 xl:hidden"
          aria-label="Editor workspace panes"
        >
          {([
            ['elements', 'Elements'],
            ['preview', 'Preview'],
            ['targeting', 'Targeting'],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setMobilePane(key)}
              aria-pressed={mobilePane === key}
              className={`rounded-full px-3 py-2 text-xs font-bold transition-colors ${
                mobilePane === key ? 'ff-segment-active' : 'text-ff-mute hover:text-ff-ink'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>

        <section className={`${mobilePane === 'elements' ? 'block' : 'hidden'} ff-card p-5 xl:block xl:max-w-none max-w-2xl xl:mx-0 mx-auto w-full xl:max-h-[calc(100vh-7rem)] xl:overflow-y-auto xl:sticky xl:top-[4.5rem]`}>
          <ElementPanel
            config={config}
            sourceFields={fieldsQ.data ?? { from_source: [], custom: [], types: {} }}
            selectedKey={selectedKey}
            onSelect={setSelectedKey}
            onPatchElement={patchElement}
            onEnableLifestyle={enableLifestyleMode}
            onDisableLifestyle={disableLifestyleMode}
            onPatchLifestyle={patchLifestyle}
            onAddCustomElement={addCustomElement}
            onRemoveElement={removeElement}
            onUploadAsset={uploadAsset}
            uploading={uploading}
            storeDefaultCutout={
              storesQ.data?.find((s) => s.id === selectedStoreId)?.default_cutout_mode ?? 'auto'
            }
            productCanvasW={productCanvasW}
          />
        </section>

        <section className={`${mobilePane === 'preview' ? 'block' : 'hidden'} space-y-5 xl:block xl:max-w-none max-w-3xl xl:mx-0 mx-auto w-full`}>
          {(samplesQ.isLoading || (samplesQ.isFetching && !samplesQ.data)) && (
            <div
              className="flex items-center gap-3 rounded-xl border border-ff-success/20 bg-ff-success-soft px-4 py-3 text-sm text-ff-success"
              data-testid="editor-samples-loading"
              role="status"
              aria-live="polite"
            >
              <span className="inline-block h-3 w-3 rounded-full bg-ff-success animate-pulse" />
              <span>
                Fetching sample products from <code className="font-data">{selectedStoreId}</code>…
                {' '}First load can take 30–60s while we cache cutouts for new product images.
              </span>
            </div>
          )}
          <div className="ff-card p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="font-mono text-[11px] uppercase tracking-widish text-ff-mute">
                Sample products
              </p>
              <button
                type="button"
                onClick={async () => {
                  await api.refreshSampleProducts();
                  samplesQ.refetch();
                }}
                className="text-[11px] font-mono uppercase tracking-widish text-ff-mute hover:text-ff-forest"
              >
                Refresh
              </button>
            </div>
            <SampleProductTabs
              products={samplesQ.data ?? []}
              selectedId={productId}
              onSelect={setProductId}
            />
            {samplesQ.isError && (
              <p className="mt-2 text-xs text-ff-danger">
                Couldn't load samples. Check that the backend is reachable.
              </p>
            )}
          </div>

          <div className="ff-card p-3 sm:p-6">
            <Canvas
              config={config}
              previewUrl={previewUrl}
              isLoading={previewing}
              lifestyleState={lifestyleState}
              lifestyleReason={lifestyleReason}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
              onPositionChange={(key, x, y) =>
                // The product image's position is part of its BOX: writing a bare
                // {x, y} would leave a legacy `scale` in place, so a drag would be
                // the one edit that doesn't convert to explicit w/h (spec B-1).
                // Byte-identical either way — only config_hash moves.
                patchElement(key, key === 'product_image'
                  ? productBoxPatch(config.elements.product_image, { x, y }, productCanvasW)
                  : { x, y })}
              onProductBoxChange={(box) => patchElement('product_image', productBoxPatch(config.elements.product_image, box, productCanvasW))}
            />
          </div>

          <SampleRenderPanel
            key={`${config.name}-${config.updated_at}`}
            storeId={selectedStoreId}
            config={config}
          />
        </section>

        <section className={`${mobilePane === 'targeting' ? 'block' : 'hidden'} ff-card p-5 xl:block xl:max-w-none max-w-2xl xl:mx-0 mx-auto w-full xl:max-h-[calc(100vh-7rem)] xl:overflow-y-auto xl:sticky xl:top-[4.5rem]`}>
          <FilterEditor
            config={config}
            sourceFields={fieldsQ.data ?? { from_source: [], custom: [], types: {} }}
            storeId={selectedStoreId}
            onPatchMeta={patchMeta}
            onPatchFilter={patchFilter}
            isDefault={isDefault}
          />
        </section>
      </main>

      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
